import React from "react";
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const ModalWrapper = ({
    open,
    setOpen,
    onClose,
    title,
    subtitle,
    actions,
    content,
    width = "50vw",
    fullHeight = false,
    fullScreen = false,
    scrollWholeModal = false,
}) => {
    const { t } = useTranslation();

    // Default translated values
    const finalTitle = title || t('TITLES.CONFIRM_ACTION');
    const finalSubtitle = subtitle || t('TITLES.REVIEW_DETAILS_AND_CONTINUE');

    const handleClose = () => {
        if (onClose) onClose();
        setOpen(false);
    };

    const isCustomTitle = React.isValidElement(title);

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: fullScreen ? 0 : "50%",
                    left: fullScreen ? 0 : "50%",
                    transform: fullScreen ? "none" : "translate(-50%, -50%)",
                    width: fullScreen ? "100vw" : { xs: "90%", sm: width },
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: fullScreen ? 0 : 2,
                    p: fullScreen ? 0 : 2,
                    outline: "none",
                    display: "flex",
                    flexDirection: "column",

                    ...(fullScreen
                        ? {
                              height: "100vh",
                              maxHeight: "100vh",
                              overflow: "hidden",
                              overflowX: "hidden",
                          }
                        : fullHeight
                        ? {
                              height: "90vh",
                              maxHeight: "90vh",
                              overflowY: scrollWholeModal ? "auto" : "hidden",
                              overflowX: "hidden",
                          }
                        : {
                              maxHeight: "80vh",
                              overflowY: scrollWholeModal ? "auto" : "visible",
                              overflowX: "hidden",
                          }),
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{
                        flexShrink: 0,
                        mb: fullScreen ? 0 : 2,
                        px: fullScreen ? { xs: 2, sm: 3 } : 0,
                        py: fullScreen ? 1.5 : 0,
                        gap: 1,
                        borderBottom: fullScreen ? "1px solid" : "none",
                        borderColor: "divider",
                    }}
                >
                    {isCustomTitle ? (
                        <Box sx={{ flex: 1, minWidth: 0 }}>{finalTitle}</Box>
                    ) : (
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                {finalTitle}
                            </Typography>
                            {subtitle && (
                                <Typography
                                    variant="body2"
                                    fontSize="11px"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    {finalSubtitle}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <IconButton
                        onClick={handleClose}
                        sx={{ flexShrink: 0, mt: fullScreen ? 0 : -0.5, mr: fullScreen ? 0 : -0.5 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {/* Content */}
                <Box
                    sx={{
                        flexGrow: scrollWholeModal ? 0 : 1,
                        flexShrink: 1,
                        overflowY: scrollWholeModal ? "visible" : "auto",
                        minHeight: scrollWholeModal ? "auto" : 0,
                    }}
                >
                    {content}
                </Box>

                {/* Actions */}
                {actions && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                            px: fullScreen ? { xs: 2, sm: 3 } : 0,
                            py: fullScreen ? 1.5 : 0,
                            pt: fullScreen ? 1.5 : 2,
                            mt: fullScreen ? 0 : 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {actions}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default ModalWrapper;
